import { mutationTypes, actionTypes } from '@/types';
import logger from '@/tools/logger';
import { uiComponents, uiStates } from '@/tools/viewState';

// Via this way bc the browser doesn't support the TCP sockets necessary for the node lib
const phidget22 = window.phidget22;

export default {
	state: {
		connectedToPhidgetServer: false,
		selectedDefaultTriggerDevice: false,
		triggeredArticles: {},
		mediaObjId: null,
		triggeredArticleId: null,
		backgroundTrigger: true,
		hardwareTrigger: false,
	},
	mutations: {
		[mutationTypes.connectionToPhidgetServer](state, { connectedToPhidgetServer }) {
			state.connectedToPhidgetServer = connectedToPhidgetServer;
		},
		[mutationTypes.selectedDefaultTriggerDevice](state, { selectedDefaultTriggerDevice }) {
			state.selectedDefaultTriggerDevice = selectedDefaultTriggerDevice;
		},
		[mutationTypes.setTriggeredArticleAndMediaIds](state, { triggerArticles, articleId, mediaId }) {
			(state.triggeredArticles = triggerArticles),
				(state.mediaObjId = mediaId),
				(state.triggeredArticleId = articleId);
		},
		[mutationTypes.setTriggeredOption](state, { backgroundTrigger, hardwareTrigger }) {
			(state.backgroundTrigger = backgroundTrigger), (state.hardwareTrigger = hardwareTrigger);
		},
	},
	actions: {
		// opening a phidget connection
		// https://www.phidgets.com/?view=api

		async [actionTypes.openPhidgetConnection]({ commit, rootState, state, dispatch }) {
			if (rootState.isPreview) return;

			if (state.connectedToPhidgetServer) return;

			// connection to the networkserver
			const connection = new phidget22.Connection({
				hostname: 'localhost',
				port: 8081,
				onError: (code, msg) => {
					logger.error('Connection Error:', msg, code);
					commit(mutationTypes.connectionToPhidgetServer, {
						connectedToPhidgetServer: false,
					});
				},
				onConnect: () => {
					commit(mutationTypes.connectionToPhidgetServer, {
						connectedToPhidgetServer: true,
					});
					dispatch(actionTypes.openPhidgetInputConnection);
					// dispatching this event because the article is of type media and it required mediaID to show the background video
					dispatch(actionTypes.filterMediaByTriggerType, { triggerType: 'background' });
				},
				onDisconnect: () => {
					logger.log('Disconnected to Phidgetserver');
					commit(mutationTypes.connectionToPhidgetServer, {
						connectedToPhidgetServer: false,
					});
				},
			});
			await connection.connect().catch((err) => {
				logger.error('Error during phidget connect:', err);
			});
		},
		// opens and configs phidget Inputconnections, gets called in loadDatasource
		// NOTE: extend here for more datastreams, connections, etc.
		// only calls if device is a mediaplayer as denoted by this urlscheme:
		// http://localhost:8080/apps/webapp-navigation-bar/?mac=00:30:59:1D:70:52&mode=mediaplayer#/61f7b14a46ce5a5475e0a341/61fd28b9b3118d6efaf9979d?ds=https%3A%2F%2Fweb.staging.shoutrlabs.com%2F61f01c7a8065df6c99b8fe43%2F&mac=94%3AC6%3A91%3AA3%3A08%3A89&mode=mediaplayer
		// @param eventHandler - String of the key of the wanted eventhandler
		[actionTypes.openPhidgetInputConnection]({ dispatch }) {
			// NOTE: Probably expand this here if you need more functionality than just 1 digital input
			let digitalInput0 = new phidget22.DigitalInput();
			digitalInput0.name = 'DigitalInput0';
			digitalInput0.setChannel(0);
			digitalInput0
				.open(100)
				.then(() => {
					logger.log('Phidget DigitalInput0 opened');
				})
				.catch((error) => {
					logger.error('Phidget DigitalInput0 connection Error', error);
				});
			digitalInput0.onStateChange = (state) => {
				if (state) {
					dispatch(actionTypes.loadTriggeredVideo, { triggerType: 'hardwareTrigger' });
				}
				if (!state) {
					dispatch(actionTypes.loadScreenSaverVideo, { triggerType: 'background' });
				}
			};
			digitalInput0.onAttach = () => {
				logger.log('attach');
			};
			digitalInput0.onDetach = () => {
				logger.log('onDetach');
			};
		},
		async [actionTypes.loadScreenSaverVideo]({ dispatch, state, commit }, { triggerType, triggerDevice }) {
			await dispatch(actionTypes.filterMediaByTriggerType, { triggerType, triggerDevice });
			commit(mutationTypes.setTriggeredOption, {
				backgroundTrigger: triggerType === 'background' ? true : false,
				hardwareTrigger: triggerType === 'hardwareTrigger' ? true : false,
			});
			let [triggerArticle] = state.triggeredArticles.articles.filter(
				(article) => article.trigger === triggerType
			);
			if (triggerArticle.type === 'editor') {
				dispatch(actionTypes.pushUrl, {
					// prettier-ignore
					url: `/${triggerArticle.exhibitionID}/${triggerArticle.id}`,
					replace: false,
				});
				commit(mutationTypes.viewStateSetUiState, {
					id: uiComponents.uiWebStationVideo,
					uiState: uiStates.hidden,
				});
			} else {
				dispatch(actionTypes.videoLoadAndPlay, {
					mediaID: state.mediaObjId,
				});
			}
		},
		async [actionTypes.loadTriggeredVideo]({ dispatch, state, commit }, { triggerType }) {
			await dispatch(actionTypes.filterMediaByTriggerType, { triggerType });
			commit(mutationTypes.setTriggeredOption, {
				backgroundTrigger: triggerType === 'background' ? true : false,
				hardwareTrigger: triggerType === 'hardwareTrigger' ? true : false,
			});
			dispatch(actionTypes.videoLoadAndPlay, {
				mediaID: state.mediaObjId,
			});
		},
		[actionTypes.filterMediaByTriggerType](
			{ rootState, commit, getters },
			{ triggerType, triggerDevice = 'phidget1011' }
		) {
			let mediaId = null;
			let articleId = null;
			const [triggerArticles] = rootState.dataSourceCFG.articleLists.filter(
				(listElement) =>
					listElement.triggerDevice === triggerDevice && listElement.macAddress === rootState.mediaplayer.mac
			);
			if (!triggerArticles) return; // no phidget controlled playlists
			let [triggerArticle] = triggerArticles.articles.filter((article) => article.trigger === triggerType);
			articleId = triggerArticle.id;
			const [langSrc] = triggerArticle.languageContent
				.filter((language) => language.languageISO === getters.currentLanguage)
				.map((lang) => lang.source);
			const mediaObj = rootState.dataSourceCFG.mediaObjectsInfo.mediaObjects;
			for (let [key, value] of Object.entries(mediaObj)) {
				if (value[getters.currentLanguage] === langSrc) {
					mediaId = key;
				}
			}
			commit(mutationTypes.setTriggeredArticleAndMediaIds, { triggerArticles, articleId, mediaId });
			commit(mutationTypes.setTriggeredOption, {
				backgroundTrigger: triggerType === 'background' ? true : false,
				hardwareTrigger: triggerType === 'hardwareTrigger' ? true : false,
			});
		},
	},
};
